import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  const getGuestCart = () => {
    try {
      const cart = localStorage.getItem('pazarlo_guest_cart');
      return cart ? JSON.parse(cart) : [];
    } catch { return []; }
  };

  const saveGuestCart = (cartItems) => {
    localStorage.setItem('pazarlo_guest_cart', JSON.stringify(cartItems));
  };

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setItems(getGuestCart());
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`);
      setItems(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    if (!isAuthenticated) {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        const product = response.data;
        const guestCart = getGuestCart();
        const existingIndex = guestCart.findIndex(item => item.id === productId);
        if (existingIndex >= 0) {
          guestCart[existingIndex].quantity += quantity;
          guestCart[existingIndex].subtotal = guestCart[existingIndex].display_price * guestCart[existingIndex].quantity;
        } else {
          guestCart.push({
            id: productId, product_id: productId, name: product.name,
            price: product.base_price || product.price,
            display_price: product.display_price || product.base_price,
            image_url: product.image_url, oem_code: product.oem_code,
            quantity: quantity,
            subtotal: (product.display_price || product.base_price) * quantity
          });
        }
        saveGuestCart(guestCart);
        setItems(guestCart);
        return { success: true };
      } catch (error) { throw error; }
    }
    try {
      const response = await axios.post(`${API}/cart`, { product_id: productId, quantity });
      await fetchCart();
      return response.data;
    } catch (error) { throw error; }
  };

  const updateQuantity = async (itemId, quantity) => {
    if (!isAuthenticated) {
      const guestCart = getGuestCart();
      const index = guestCart.findIndex(item => item.id === itemId);
      if (index >= 0) {
        if (quantity <= 0) { guestCart.splice(index, 1); }
        else { guestCart[index].quantity = quantity; guestCart[index].subtotal = guestCart[index].display_price * quantity; }
        saveGuestCart(guestCart);
        setItems(guestCart);
      }
      return;
    }
    try { await axios.put(`${API}/cart/${itemId}?quantity=${quantity}`); await fetchCart(); }
    catch (error) { throw error; }
  };

  const removeFromCart = async (itemId) => {
    if (!isAuthenticated) {
      const guestCart = getGuestCart().filter(item => item.id !== itemId);
      saveGuestCart(guestCart);
      setItems(guestCart);
      return;
    }
    try { await axios.delete(`${API}/cart/${itemId}`); await fetchCart(); }
    catch (error) { throw error; }
  };

  const clearCart = async () => {
    if (!isAuthenticated) { saveGuestCart([]); setItems([]); return; }
    try { await axios.delete(`${API}/cart`); setItems([]); }
    catch (error) { throw error; }
  };

  const totalItems = (items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);
  const totalPrice = (items || []).reduce((sum, item) => sum + (item.subtotal || 0), 0);

  return (
    <CartContext.Provider value={{ items, loading, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, totalItems, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
